import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseLocalePath, localePath as buildLocalePath } from '../i18n/routes';
import { DEFAULT_LOCALE, type Locale } from '../i18n/config';

interface LocaleContextType {
  locale: Locale;
  localePath: (path: string, params?: Record<string, string>) => string;
}

const LocaleContext = createContext<LocaleContextType>({
  locale: DEFAULT_LOCALE,
  localePath: (path) => path,
});

export const useLocale = () => useContext(LocaleContext);

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { i18n } = useTranslation();

  const locale = useMemo(() => {
    const { locale } = parseLocalePath(location.pathname);
    return locale;
  }, [location.pathname]);

  // Sync i18next language when locale changes
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  const localePath = useMemo(
    () => (path: string, params?: Record<string, string>) =>
      buildLocalePath(locale, path, params),
    [locale]
  );

  const value = useMemo(() => ({ locale, localePath }), [locale, localePath]);

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;
